import {
    activate,
    ensureInitialized,
    fetchConfig,
    getRemoteConfig,
    getValue,
    isSupported,
} from "firebase/remote-config";

import { firebaseApp } from "../../firebase";
import {
    BooleanConfigSource,
    BooleanFeatureFlagKey,
    BooleanFeatureFlags,
    booleanFeatureFlagDefaults,
} from "./BooleanFeatureFlags";

export const booleanFeatureFlags = new BooleanFeatureFlags(async (): Promise<BooleanConfigSource | null> => {
    if (typeof window === "undefined" || !(await isSupported())) return null;

    const config = getRemoteConfig(firebaseApp);
    config.defaultConfig = booleanFeatureFlagDefaults;
    // Default SDK cache age is 12 hours. Request a server check on every new document load.
    config.settings.minimumFetchIntervalMillis = 0;

    return {
        async fetchFresh() {
            await ensureInitialized(config);
            const previousFetchTime = config.fetchTimeMillis;
            await fetchConfig(config);
            // With a zero interval, a cache entry from this same millisecond may still count as fresh.
            if (config.lastFetchStatus !== "success" || config.fetchTimeMillis <= previousFetchTime) {
                throw new Error("Remote Config was not confirmed by a new server fetch");
            }
            await activate(config);
        },
        read(key: BooleanFeatureFlagKey) {
            const value = getValue(config, key);
            return value.getSource() === "remote" && value.asString().toLowerCase() === "true";
        },
    };
});
